import { jsPDF } from "jspdf";
import { logoBase64 } from "@/utils/pdfLogo";
import { eSignatureLee, eSignatureMelanie } from "@/utils/eSignatureDoctors.js";

import moment from "moment";

export default function pdfMedCertGenerateHandler() {
  const safeText = (value) => (value ? String(value).trim() : "N/A");

  // --- Add LOGO ONLY header for page 2+ ---
  const addSecondaryHeader = (doc, pageWidth) => {
    const logoWidth = 90;
    const logoHeight = 22;

    const logoX = (pageWidth - logoWidth) / 2;

    try {
      doc.addImage(
        logoBase64,
        "JPEG",
        logoX,
        10,
        logoWidth,
        logoHeight,
        "",
        "FAST",
      );
    } catch (err) {
      console.warn("⚠️ Logo could not be added:", err);
    }
  };

  // --- Footer ---
  const addFooter = (doc, pageWidth, pageHeight, pageNumber, totalPages) => {
    const footerY = pageHeight - 12;

    doc.setFont("helvetica", "normal");

    doc.setFontSize(9);

    doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth / 2, footerY, {
      align: "center",
    });
  };

  const exportMedCertPDF = async (item, detail) => {
    if (!item || !item.length) {
      console.error("❌ Invalid data: 'item' is empty or undefined.");

      return;
    }

    const data = item[0];

    const doc = new jsPDF({
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageWidth = doc.internal.pageSize.getWidth();

    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 10;

    let y = 115;

    let currentPage = 1;

    const patientAge = moment().diff(moment(data.birthdate), "years");

    // --- FULL HEADER (page 1) ---
    const addHeader = () => {
      const logoWidth = 90;

      const logoHeight = 22;

      const logoX = (pageWidth - logoWidth) / 2;

      try {
        doc.addImage(
          logoBase64,
          "JPEG",
          logoX,
          10,
          logoWidth,
          logoHeight,
          "",
          "FAST",
        );
      } catch (err) {
        console.warn("⚠️ Logo could not be added:", err);
      }

      doc.setFont("helvetica", "normal");

      doc.setFontSize(13);

      doc.text(safeText(data.doctor_name), pageWidth / 2, 40, {
        align: "center",
      });

      doc.setFontSize(11);

      doc.text(safeText(data.specialization), pageWidth / 2, 46, {
        align: "center",
      });

      doc.text(`Patient Name: ${safeText(data.membername)}`, 10, 63);

      doc.text(
        `Date: ${moment(detail.createdat).format("LL") || "N/A"}`,
        pageWidth - 10,
        63,
        {
          align: "right",
        },
      );

      doc.text(`Birthdate: ${moment(data.birthdate).format("LL")}`, 10, 71);
    };

    const addTitle = () => {
      doc.setFont("helvetica", "bold");

      doc.setFontSize(17);

      doc.text("M E D I C A L   C E R T I F I C A T E", pageWidth / 2, 90, {
        align: "center",
      });
    };

    // --- Page break helper ---
    const checkAddPage = (
      docRef,
      yPos,
      lineHeight,
      pageHeightRef,
      marginRef,
      pageWidthRef,
    ) => {
      if (yPos + lineHeight > pageHeightRef - marginRef) {
        docRef.addPage();

        currentPage++;

        if (currentPage >= 2) {
          addSecondaryHeader(docRef, pageWidthRef);
        }

        return marginRef + 25;
      }

      return yPos;
    };

    // --- BODY ---
    const addBody = () => {
      const lineHeight = 8;

      let yPos = y;

      const wrapWidth = pageWidth - 20;

      const addWrappedText = (text, options = {}) => {
        const { font = "normal", italic = false, extraSpace = true } = options;

        doc.setFont("helvetica", italic ? "italic" : font);

        const lines = doc.splitTextToSize(safeText(text), wrapWidth);

        lines.forEach((line) => {
          yPos = checkAddPage(
            doc,
            yPos,
            lineHeight,
            pageHeight,
            margin,
            pageWidth,
          );

          doc.text(line, margin, yPos);

          yPos += lineHeight;
        });

        if (extraSpace) {
          yPos += lineHeight;
        }

        return yPos;
      };

      doc.setFont("helvetica", "normal");

      doc.setFontSize(12);

      // --- Intro sentence ---
      const intro = `This is to certify that `;

      const name = `${safeText(data.membername)}`;

      const ageText = `, ${safeText(
        patientAge,
      )} years of age, consulted on ${moment(detail.createdat).format("LL")}`;

      doc.text(intro, margin, yPos);

      const introWidth = doc.getTextWidth(intro);

      doc.setFont("helvetica", "bold");

      doc.text(name, margin + introWidth, yPos);

      const nameWidth = doc.getTextWidth(name);

      doc.setFont("helvetica", "normal");

      doc.text(ageText, margin + introWidth + nameWidth, yPos);

      yPos += lineHeight * 1.5;

      // --- Clinical Impression ---
      yPos = checkAddPage(doc, yPos, lineHeight, pageHeight, margin, pageWidth);

      doc.setFont("helvetica", "bold");

      doc.text("Clinical Impression:", margin, yPos);

      yPos += lineHeight;

      yPos = addWrappedText(`${safeText(data.impression)}.`, {
        italic: true,
      });

      // --- Number of Days ---
      const numberDays = data.numberdays ?? 0;

      if (Number(numberDays) > 0) {
        yPos = checkAddPage(
          doc,
          yPos,
          lineHeight,
          pageHeight,
          margin,
          pageWidth,
        );

        const daysText = `He/She would need medical attention/rest for ${safeText(
          numberDays,
        )} day(s) barring complications.`;

        yPos = addWrappedText(daysText);
      }

      // --- Recommendations ---
      yPos = checkAddPage(doc, yPos, lineHeight, pageHeight, margin, pageWidth);

      doc.setFont("helvetica", "bold");

      doc.text("Further Recommendations:", margin, yPos);

      yPos += lineHeight;

      yPos = addWrappedText(data.recommendations || "N/A", {
        italic: true,
      });

      // --- Disclaimer ---
      yPos = checkAddPage(doc, yPos, lineHeight, pageHeight, margin, pageWidth);

      yPos = addWrappedText(
        data.disclaimer ||
          "This certificate is issued upon request of the above-mentioned for whatever purpose it may serve, except medico-legal purposes.",
      );

      return yPos;
    };

    // --- SIGNATURE ---
    const addSignature = (currentY) => {
      const lineHeight = 8;

      let yPos = currentY + lineHeight * 0.8;

      const requiredSpace = 55;

      if (yPos + requiredSpace > pageHeight - 20) {
        doc.addPage();

        currentPage++;

        if (currentPage >= 2) {
          addSecondaryHeader(doc, pageWidth);
        }

        yPos = 38;
      }

      const signatureY = yPos + 30;

      const lineWidth = 80;

      const marginRight = 24;

      const lineX = pageWidth - lineWidth - marginRight;

      const marginX = lineX + lineWidth / 2 - 15;

      try {
        if (data.doctor_id === 1) {
          doc.addImage(eSignatureLee, "PNG", marginX, yPos, 35, 35);
        }

        if (data.doctor_id === 13) {
          doc.addImage(eSignatureMelanie, "PNG", marginX, yPos, 35, 35);
        }
      } catch (err) {
        console.warn("⚠️ Signature image could not be added:", err);
      }

      doc.setFont("helvetica", "bold");

      doc.setFontSize(13);

      doc.text(safeText(data.doctor_name), lineX + lineWidth / 2, signatureY, {
        align: "center",
      });

      doc.setFont("helvetica", "normal");

      doc.setFontSize(11);

      doc.text(
        `License No: ${safeText(data.license_number)}`,
        lineX + lineWidth / 2,
        signatureY + 7,
        {
          align: "center",
        },
      );

      doc.text(
        `PTR No: ${safeText(data.ptr_number)}`,
        lineX + lineWidth / 2,
        signatureY + 14,
        {
          align: "center",
        },
      );
    };

    // --- GENERATE ---
    addHeader();

    addTitle();

    const finalY = addBody();

    addSignature(finalY);

    // --- FOOTERS ---
    const totalPages = doc.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      addFooter(doc, pageWidth, pageHeight, i, totalPages);
    }

    // === SAVE LOCAL PDF ===
    const transactionId = safeText(data.transactionid);

    doc.save(`MEDICAL_CERTIFICATION_${transactionId || Date.now()}.pdf`);
  };

  return { exportMedCertPDF };
}
